import React, { useState, useEffect, useContext, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Grid, Box, FormControl, FormLabel, FormGroup, FilledInput, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { DataGrid } from '@material-ui/data-grid';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import Loader from '../../common/loader';
import NotFound from '../../common/not-found';

import AuthContext from '../../context';
import { get } from '../../utils/api-services';
import helpers from '../../utils/helpers';

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        paddingBottom: 20
    },
    tableContainer: {
        width: '100%'
    }
}));

const defDepartment = { id: 0, name: 'All departments' };

const LeaderboardDepartment = () => {

    const isMountedRef = useRef(false);
    const departmentFilterRef = useRef(defDepartment);
    const dateFilterRef = useRef(dayjs().format('YYYY-MM-DD'));
    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const classes = useStyles();
    const { pathname } = useLocation();
    const [state, setState] = useState({
        loading: true,
        departments: [],
        filters: {
            department: defDepartment,
            date: dayjs().format('YYYY-MM-DD'),
            lastUpdate: dayjs().format('HH:mm')
        },
        data: [],
        permissions: {}
    });

    useEffect(() => {
        window.document.title = 'Leaderboard (Department)';

        validatePermissions();

        const intervalId = setInterval(() => intervalFunction(), 300000);

        return () => {
            clearInterval(intervalId);
        }
    }, []);

    useEffect(() => {
        departmentFilterRef.current = state.filters.department;
        dateFilterRef.current = state.filters.date;
        intervalFunction();

    }, [state.filters.department, state.filters.date])

    const validatePermissions = () => {
        const permissions = helpers.getPermissions(pathname);
        setState(prev => ({ ...prev, permissions }));

        (permissions.read_attr) ? makeRequest() : setState(prev => ({ ...prev, loading: false }));
    }

    const intervalFunction = async () => {
        if (isMountedRef.current) {
            const newLastUpdate = dayjs().format('HH:mm');
            const currentDepartment = departmentFilterRef.current;
            const currentDate = dateFilterRef.current;

            await search(newLastUpdate, currentDepartment, currentDate);
        }
    }

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/productionDepartmentLeaderboard');
        if (response.status === 200) {
            setState(prev => ({ ...prev, loading: false, data: response?.data ?? [], departments: response?.departments ?? [] }));
            isMountedRef.current = true;
        } else {
            console.log('[LeaderboardDepartment] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const search = async (lastUpdate, department, date) => {
        showLoader(true);
        const response = await get('/productionDepartmentLeaderboard/search', { ...state.filters, department, date });
        if (response.status === 200) {
            setState(prev => ({ ...prev, data: response?.data ?? [], filters: { ...prev.filters, lastUpdate } }));
        } else {
            console.log('[LeaderboardDepartment] search error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleChangeAutocomplete = (key, value) => setState(prev => ({ ...prev, filters: { ...prev.filters, [key]: value } }));
    const handleChangeDate = ({ target }) => setState(prev => ({ ...prev, filters: { ...prev.filters, [target.name]: target.value } }));

    const valueGetter = (item) => Number(item.value);

    const columns = [
        { field: 'position', headerName: '-', flex: 0.5 },
        { field: 'code', headerName: 'Employee code', flex: 1 },
        { field: 'name', headerName: 'Employee name', flex: 1 },
        { field: 'productions.product.code', headerName: 'Product code', flex: 1 },
        { field: 'productions.product.description', headerName: 'Product name', flex: 1 },
        { field: 'productions.pack_size', headerName: 'Pack size', flex: 1, type: 'number', valueGetter },
        { field: 'productions.qty', headerName: 'Qty', flex: 1, type: 'number', valueGetter },
        { field: 'productions.total_qty', headerName: 'Total', flex: 1, type: 'number', valueGetter }
    ];

    if (state.loading) return <Loader />;
    if (!state.permissions.read_attr) return <NotFound />;

    return (
        <div className={classes.root}>
            <Grid container>
                <Grid item xs={12} sm={6} lg={6}>
                    <FormGroup>
                        <FormLabel>{'Department'}</FormLabel>
                        <Autocomplete
                            options={state.departments}
                            value={state.filters.department}
                            getOptionLabel={option => option?.name ?? ''}
                            getOptionSelected={(option, value) => option.id === value.id}
                            onChange={(e, value) => handleChangeAutocomplete('department', value)}
                            renderInput={params => <TextField {...params} placeholder='Department' />}
                        />
                    </FormGroup>
                </Grid>
                <Grid item xs={6} md={3}>
                    <FormGroup>
                        <FormLabel>{'Date'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                autoComplete='off'
                                type='date'
                                placeholder='Date'
                                name='date'
                                value={state.filters.date}
                                onChange={handleChangeDate}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
                <Grid item xs={6} md={3}>
                    <FormGroup>
                        <FormLabel>{'Last update'}</FormLabel>
                        <FormControl
                            variant='filled'
                            component={Box}
                            width='100%'
                        >
                            <FilledInput
                                disabled
                                type='text'
                                value={state.filters.lastUpdate}
                            />
                        </FormControl>
                    </FormGroup>
                </Grid>
            </Grid>

            <div className={classes.tableContainer}>
                <DataGrid
                    autoHeight
                    disableSelectionOnClick
                    rows={state.data}
                    columns={columns}
                    pageSize={45}
                    density='compact'
                    getRowId={row => `${row['idemployee']}-${row['productions.idproduct_sale']}`}
                />
            </div>
        </div>
    );
}

export default LeaderboardDepartment;